/**
 * Pause/play control for the hero's decorative motion: the full-bleed
 * background video and any typewriter headline inside the same section.
 *
 * The DOM's own initial state is "playing" — background videos carry the
 * autoplay attribute and CA.init_typewriter queues the intro-delayed start —
 * so this controller tracks that as its starting point and only touches the
 * targets when the desired state differs. That keeps the original delay before
 * the headline starts typing intact instead of restarting it here.
 *
 * initHeroControls() is idempotent (it destroys the previous page's instances
 * first, so it is safe on every Barba transition) and must run after
 * CA.init_typewriter(), which creates the handles it drives.
 */
import { createMotionToggle, setTogglePressed } from "./motion-toggle.js";
import { watchMediaQuery, prefersReducedMotion, REDUCED_MOTION_QUERY } from "./util.js";

var instances = [];

// Background videos are ours to drive. One with its own controls belongs to
// the visitor, so leave it alone.
function collectVideos(hero) {
    var all = hero.querySelectorAll("video");
    var out = [];
    for (var i = 0; i < all.length; i++) {
        if (all[i].autoplay && !all[i].controls) out.push(all[i]);
    }
    return out;
}

function collectTypewriters(hero) {
    var all = hero.querySelectorAll("[data-type]");
    var out = [];
    for (var i = 0; i < all.length; i++) {
        if (all[i].__caTypewriter) out.push(all[i].__caTypewriter);
    }
    return out;
}

function createHeroControl(hero) {
    var videos = collectVideos(hero);
    var typewriters = collectTypewriters(hero);

    // Nothing moves in this hero — a pause button would be a lie.
    if (!videos.length && !typewriters.length) return null;

    var destroyed = false;
    var playing = true;       // the state the markup already starts in
    var userPaused = false;
    var userExplicitlyToggled = false;
    var intersecting = true;  // a hero loads in view; the observer corrects it

    function playVideo(video) {
        var promise = video.play();
        // Autoplay policies can reject this; nothing to recover, so swallow it
        // rather than surfacing an unhandled rejection.
        if (promise && promise.catch) promise.catch(function () {});
    }

    function updatePlaying() {
        var should = !destroyed && !userPaused && intersecting && !document.hidden;
        if (should === playing) return;
        playing = should;
        var i;
        for (i = 0; i < videos.length; i++) {
            if (playing) playVideo(videos[i]);
            else videos[i].pause();
        }
        for (i = 0; i < typewriters.length; i++) {
            if (playing) typewriters[i].start();
            else typewriters[i].stop();
        }
    }

    function setUserPaused(v) {
        userPaused = v;
        setTogglePressed(button, v);
        updatePlaying();
    }

    function onToggle() {
        userExplicitlyToggled = true;
        setUserPaused(!userPaused);
    }

    var button = createMotionToggle("Pause header animation", onToggle);
    hero.appendChild(button);

    // Reduced motion starts the hero paused: the headline still shows its
    // first phrase and the video its first frame. An explicit press always
    // outranks the media query from then on.
    function onReducedMotionChange(e) {
        if (e.matches) setUserPaused(true);
        else if (!userExplicitlyToggled) setUserPaused(false);
    }

    var unwatchReducedMotion = watchMediaQuery(REDUCED_MOTION_QUERY, onReducedMotionChange);
    setUserPaused(prefersReducedMotion());

    var io = null;
    if (typeof IntersectionObserver !== "undefined") {
        io = new IntersectionObserver(
            function (entries) {
                intersecting = entries[entries.length - 1].isIntersecting;
                updatePlaying();
            },
            { rootMargin: "100px", threshold: 0 }
        );
        io.observe(hero);
    }

    function onVisibilityChange() {
        updatePlaying();
    }

    document.addEventListener("visibilitychange", onVisibilityChange);

    return {
        destroy: function () {
            if (destroyed) return;
            destroyed = true;
            if (io) io.disconnect();
            unwatchReducedMotion();
            document.removeEventListener("visibilitychange", onVisibilityChange);
            button.removeEventListener("click", onToggle);
            button.remove();
        },
    };
}

export default function initHeroControls() {
    instances.forEach(function (inst) {
        inst.destroy();
    });
    instances = [];
    var heroes = document.querySelectorAll("section.hero");
    for (var i = 0; i < heroes.length; i++) {
        var inst = createHeroControl(heroes[i]);
        if (inst) instances.push(inst);
    }
}
